import React from 'react';
import { Link } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { usePreferences } from './Preferences/PreferencesContext';
import { getThemeColors } from '../helpers/themeColors';
import NavigationBar from './Shared/NavigationBar';

const SPACING = {
  containerMaxWidth: 800,
  containerPaddingDesktop: 40,
  containerPaddingMobile: 16,
  sectionMarginBottom: 40,
  headingMarginBottom: 12,
  paragraphMarginBottom: 8,
};

const FONT = {
  title: 28,
  titleMobile: 22,
  heading: 18,
  body: 15,
  bodyLineHeight: 1.7,
  lastUpdated: 13,
};

const buildColors = (theme) => {
  const t = getThemeColors(theme);
  return {
    text: t.textPrimary,
    body: t.textSecondary,
    link: t.accentPrimary,
    divider: t.borderPrimary,
    muted: t.textFaint,
  };
};

const containerStyle = {
  maxWidth: SPACING.containerMaxWidth,
  margin: '0 auto',
  padding: isMobile ? SPACING.containerPaddingMobile : SPACING.containerPaddingDesktop,
};

const sectionStyle = {
  marginBottom: SPACING.sectionMarginBottom,
};

// Last section omits bottom margin so the footer sits flush
const lastSectionStyle = {};

const LAST_UPDATED = 'April 10, 2026';

const TermsOfService = () => {
  const { preferences } = usePreferences();
  const COLORS = buildColors(preferences.theme);

  const titleStyle = {
    fontSize: isMobile ? FONT.titleMobile : FONT.title,
    color: COLORS.text,
    marginBottom: SPACING.paragraphMarginBottom,
  };

  const lastUpdatedStyle = {
    fontSize: FONT.lastUpdated,
    color: COLORS.muted,
    marginBottom: SPACING.sectionMarginBottom,
  };

  const headingStyle = {
    fontSize: FONT.heading,
    color: COLORS.text,
    marginBottom: SPACING.headingMarginBottom,
    paddingBottom: SPACING.paragraphMarginBottom,
    borderBottom: `1px solid ${COLORS.divider}`,
  };

  const bodyStyle = {
    fontSize: FONT.body,
    color: COLORS.body,
    lineHeight: FONT.bodyLineHeight,
    marginBottom: SPACING.paragraphMarginBottom,
  };

  const linkStyle = {
    color: COLORS.link,
  };

  return (
    <>
      <NavigationBar />
      <div style={containerStyle}>
        <h1 style={titleStyle}>Terms of Service</h1>
        <p style={lastUpdatedStyle}>Last updated: {LAST_UPDATED}</p>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Acceptance of Terms</h2>
          <p style={bodyStyle}>
            By accessing or using CollectNDeck, you agree to be bound by these Terms of Service.
            If you do not agree to these terms, do not use the service. Your continued use of
            CollectNDeck following any updates to these terms constitutes acceptance of those
            changes.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Eligibility</h2>
          <p style={bodyStyle}>
            You must be at least 13 years of age to use CollectNDeck. By using the service, you
            represent that you meet this requirement. If you are under 13, do not create an account
            or use this service.
          </p>
          <p style={bodyStyle}>
            If you are between 13 and 18 years of age, you represent that you have obtained
            consent from a parent or legal guardian to use the service and agree to these terms.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Free Service and Advertising</h2>
          <p style={bodyStyle}>
            CollectNDeck is and will always be free to use. You will never be charged for access
            to any feature of the service.
          </p>
          <p style={bodyStyle}>
            CollectNDeck is supported by advertising revenue. By using the service, you
            acknowledge that advertisements may be displayed alongside content. Ads are served by
            third-party advertising networks, which may use cookies or similar technologies to
            serve relevant ads. You can learn more about how ad-related data is handled in our{' '}
            <Link to="/privacy" style={linkStyle}>
              Privacy Policy
            </Link>
            .
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Permitted Use</h2>
          <p style={bodyStyle}>
            CollectNDeck is provided for personal, non-commercial use only. You may use the
            service to search for Magic: The Gathering card information, browse sets, reference
            rules, and manage your personal card collection and decks.
          </p>
          <p style={bodyStyle}>
            You may not use CollectNDeck for automated scraping, bulk data harvesting, or any
            use that places excessive load on the service or its upstream data providers.
            Commercial use of any kind requires prior written permission.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Account Responsibilities</h2>
          <p style={bodyStyle}>
            You are responsible for maintaining the confidentiality of your account credentials.
            Do not share your password with others. CollectNDeck will never ask for your password
            via email or any channel other than the login form.
          </p>
          <p style={bodyStyle}>
            You are responsible for all activity that occurs under your account. If you believe
            your account has been compromised, contact us immediately at{' '}
            <a href="mailto:collectndeck@gmail.com" style={linkStyle}>
              collectndeck@gmail.com
            </a>
            .
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Prohibited Use</h2>
          <p style={bodyStyle}>
            You may not use CollectNDeck to:
          </p>
          <p style={bodyStyle}>
            <strong>Circumvent authentication:</strong> Attempt to access accounts, systems, or
            data that you are not authorized to access.
          </p>
          <p style={bodyStyle}>
            <strong>Upload harmful content:</strong> Introduce malware, scripts, or any content
            intended to damage, disrupt, or gain unauthorized access to any system.
          </p>
          <p style={bodyStyle}>
            <strong>Infringe copyright:</strong> Use the service in a way that violates the
            intellectual property rights of Wizards of the Coast, Scryfall, or any other party.
          </p>
          <p style={bodyStyle}>
            <strong>Abuse the service:</strong> Engage in behavior that degrades the experience
            for other users or interferes with the operation of the service.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Intellectual Property</h2>
          <p style={bodyStyle}>
            CollectNDeck does not claim ownership of any Magic: The Gathering intellectual
            property. All Magic: The Gathering card names, artwork, rules text, set symbols, and
            related content are the property of Wizards of the Coast LLC.
          </p>
          <p style={bodyStyle}>
            Card data, prices, and images are provided by{' '}
            <a href="https://scryfall.com" target="_blank" rel="noreferrer" style={linkStyle}>
              Scryfall
            </a>
            . CollectNDeck is unofficial Fan Content permitted under the{' '}
            <a
              href="https://company.wizards.com/en/legal/fancontentpolicy"
              target="_blank"
              rel="noreferrer"
              style={linkStyle}
            >
              Wizards of the Coast Fan Content Policy
            </a>
            . Not approved/endorsed by Wizards.
          </p>
          <p style={bodyStyle}>
            The CollectNDeck application code and any original UI design are the property of
            CollectNDeck. You may not reproduce or redistribute them without permission.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Disclaimer of Warranties</h2>
          <p style={bodyStyle}>
            CollectNDeck is provided "as is" and "as available" without warranties of any kind,
            either express or implied. We do not warrant that the service will be uninterrupted,
            error-free, or free of harmful components.
          </p>
          <p style={bodyStyle}>
            Card data, prices, and rules information are sourced from third parties and may
            contain errors or be out of date. CollectNDeck makes no guarantee as to the accuracy,
            completeness, or fitness for any purpose of this information.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Limitation of Liability</h2>
          <p style={bodyStyle}>
            To the fullest extent permitted by applicable law, CollectNDeck shall not be liable
            for any indirect, incidental, special, consequential, or punitive damages, including
            but not limited to loss of data, loss of profits, or service interruption, arising
            from your use of or inability to use the service.
          </p>
          <p style={bodyStyle}>
            CollectNDeck is provided free of charge. CollectNDeck's total liability to you for
            any claim arising out of or relating to these terms or the service shall not exceed
            one hundred US dollars (USD $100).
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Termination</h2>
          <p style={bodyStyle}>
            CollectNDeck reserves the right to suspend or terminate your account if you violate
            these Terms of Service or engage in behavior that is harmful to the service, its
            users, or third parties. For serious violations (including but not limited to
            security breaches, illegal activity, or abuse of the service), CollectNDeck may act
            immediately and without prior notice. For other violations, we will make reasonable
            efforts to provide notice before taking action.
          </p>
          <p style={bodyStyle}>
            You may delete your account at any time by contacting us at{' '}
            <a href="mailto:collectndeck@gmail.com" style={linkStyle}>
              collectndeck@gmail.com
            </a>
            . Upon termination, your data will be deleted in accordance with our{' '}
            <Link to="/privacy" style={linkStyle}>
              Privacy Policy
            </Link>
            .
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Changes to Terms</h2>
          <p style={bodyStyle}>
            CollectNDeck may update these Terms of Service at any time. When we do, the "Last
            updated" date at the top of this page will be revised. Your continued use of the
            service after changes are posted constitutes your acceptance of the revised terms.
          </p>
          <p style={bodyStyle}>
            We encourage you to review these terms periodically. For significant changes, we will
            make reasonable efforts to notify users via the app.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Governing Law</h2>
          <p style={bodyStyle}>
            These Terms of Service are governed by and construed in accordance with the laws of
            the State of Kansas, United States, without regard to its conflict of law principles.
            Any legal action or proceeding arising out of or relating to these terms shall be
            brought exclusively in the state or federal courts located in Kansas.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Dispute Resolution</h2>
          <p style={bodyStyle}>
            If you have a dispute with CollectNDeck, please contact us first at{' '}
            <a href="mailto:collectndeck@gmail.com" style={linkStyle}>
              collectndeck@gmail.com
            </a>
            . We will make a good-faith effort to resolve the matter informally within 30 days.
          </p>
          <p style={bodyStyle}>
            If the dispute cannot be resolved informally, it shall be subject to the exclusive
            jurisdiction of the courts specified in the Governing Law section above. You agree
            to submit to the personal jurisdiction of those courts for the purpose of litigating
            any such dispute.
          </p>
        </div>

        <div style={lastSectionStyle}>
          <h2 style={headingStyle}>Contact</h2>
          <p style={bodyStyle}>
            For questions or concerns about these Terms of Service, please contact us at:
          </p>
          <p style={bodyStyle}>
            <strong>Email:</strong>{' '}
            <a href="mailto:collectndeck@gmail.com" style={linkStyle}>
              collectndeck@gmail.com
            </a>
          </p>
        </div>
      </div>
    </>
  );
};

export default TermsOfService;
